import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { api } from '../api';

const AmenityBookingCalendar = ({ amenityType, amenitySettings, onBook, onClose }) => {
  const { user } = useContext(AuthContext);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    hours_start = "07:00",
    hours_end = "23:00",
    always_open = false,
    max_capacity = 1,
    block_minutes = 60
  } = amenitySettings || {};

  useEffect(() => {
    generateTimeSlots();
    loadBookings();
  }, [selectedDate, amenityType]);

  const generateTimeSlots = () => {
    const slots = [];
    const startHour = always_open ? 0 : parseInt(hours_start.split(':')[0]);
    const startMinute = always_open ? 0 : parseInt(hours_start.split(':')[1]);
    const endHour = always_open ? 24 : parseInt(hours_end.split(':')[0]);
    const endMinute = always_open ? 0 : parseInt(hours_end.split(':')[1]);

    let currentTime = new Date(selectedDate);
    currentTime.setHours(startHour, startMinute, 0, 0);

    const endTime = new Date(selectedDate);
    endTime.setHours(endHour, endMinute, 0, 0);

    while (currentTime < endTime) {
      const timeString = currentTime.toTimeString().slice(0, 5);
      const endSlotTime = new Date(currentTime.getTime() + block_minutes * 60000);
      const endTimeString = endSlotTime.toTimeString().slice(0, 5);
      
      slots.push({
        start: timeString,
        end: endTimeString,
        datetime: new Date(currentTime)
      });

      currentTime = endSlotTime;
    }

    setAvailableSlots(slots);
  };

  const loadBookings = async () => {
    if (!user?.hoa_id || !amenityType) return;
    
    setLoading(true);
    try {
      // FIX: Use local date string to avoid timezone shifts
      const dateStr = selectedDate.getFullYear() + '-' + 
                     String(selectedDate.getMonth() + 1).padStart(2, '0') + '-' + 
                     String(selectedDate.getDate()).padStart(2, '0');
      const response = await api.get('/api/amenities/availability', {
        params: {
          amenity_type: amenityType,
          date: dateStr,
          hoa_id: user.hoa_id
        }
      });
      
      if (response.data.success) {
        setAvailableSlots(response.data.slots);
      }
    } catch (error) {
      console.error('Failed to load availability:', error);
      // Fallback to generated slots if API fails
      generateTimeSlots();
    } finally {
      setLoading(false);
    }
  };

  const getSlotAvailability = (slot) => {
    // If we're using API slots, they already have availability info
    if (slot.available !== undefined) {
      return {
        available: slot.available,
        bookedCount: slot.total_capacity - slot.available_capacity,
        remainingSlots: slot.available_capacity,
        totalCapacity: slot.total_capacity
      };
    }

    // Fallback for generated slots
    // FIX: Use local date string to avoid timezone shifts
    const dateStr = selectedDate.getFullYear() + '-' + 
                   String(selectedDate.getMonth() + 1).padStart(2, '0') + '-' + 
                   String(selectedDate.getDate()).padStart(2, '0');
    
    const bookedCount = bookings.filter(booking => 
      booking.start_time === slot.start && 
      booking.date === dateStr
    ).length;

    return {
      available: bookedCount < max_capacity,
      bookedCount,
      remainingSlots: max_capacity - bookedCount,
      totalCapacity: max_capacity
    };
  };

  const handleSlotClick = async (slot) => {
    if (!slot.available) return;

    // Prompt for guest count
    const guestCount = prompt('Number of guests (including yourself):', '1');
    if (!guestCount || isNaN(guestCount) || guestCount < 1) return;

    const eventType = prompt('Event type (optional):', '');
    const specialRequests = prompt('Special requests (optional):', '');

    const bookingData = {
      amenity_type: amenityType,
      date: selectedDate.toISOString().split('T')[0],
      start_time: slot.start_time,
      end_time: slot.end_time,
      guest_count: parseInt(guestCount),
      event_type: eventType || undefined,
      special_requests: specialRequests || undefined
    };

    try {
      setLoading(true);
      const response = await api.post('/api/amenities/bookings', bookingData, {
        params: { hoa_id: user.hoa_id }
      });
      
      if (response.data.success) {
        alert('✅ Booking confirmed successfully!');
        await loadBookings(); // Refresh availability
        if (onBook) onBook(bookingData);
      }
    } catch (error) {
      console.error('Failed to create booking:', error);
      if (error.response?.status === 400) {
        alert('❌ ' + (error.response.data.detail || 'Booking conflict detected'));
      } else {
        alert('❌ Failed to create booking. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPast = (slot) => {
    if (!isToday(selectedDate)) return selectedDate < new Date();
    
    const now = new Date();
    const slotTime = new Date(selectedDate);
    const startTime = slot.start_time || slot.start;
    const [hours, minutes] = startTime.split(':');
    slotTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    return slotTime < now;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-semibold capitalize">{amenityType} Reservation</h2>
              <p className="text-gray-600">Select a time slot for your reservation</p>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              ✕
            </button>
          </div>

          {/* Date Navigation */}
          <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 rounded-lg">
            <button 
              onClick={() => navigateDate(-1)}
              className="px-3 py-2 bg-white border rounded-md hover:bg-gray-50"
            >
              ← Previous Day
            </button>
            
            <div className="text-center">
              <h3 className="text-lg font-semibold">{formatDate(selectedDate)}</h3>
              <p className="text-sm text-gray-600">
                {always_open ? 'Available 24/7' : `Available ${hours_start} - ${hours_end}`}
              </p>
            </div>
            
            <button 
              onClick={() => navigateDate(1)}
              className="px-3 py-2 bg-white border rounded-md hover:bg-gray-50"
            >
              Next Day →
            </button>
          </div>

          {/* Capacity Info */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-blue-900">{amenityType} Information</h4>
                <p className="text-sm text-blue-700">
                  {max_capacity === 1 
                    ? 'Exclusive use - only one reservation per time slot'
                    : `Shared amenity - up to ${max_capacity} reservations per ${block_minutes}-minute slot`
                  }
                </p>
              </div>
              <div className="text-right">
                <div className="text-sm text-blue-600">Slot Duration</div>
                <div className="font-semibold text-blue-900">{block_minutes} minutes</div>
              </div>
            </div>
          </div>

          {/* Time Slots Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {availableSlots.map((slot, index) => {
              const availability = getSlotAvailability(slot);
              const isSlotPast = isPast(slot);
              
              return (
                <button
                  key={index}
                  onClick={() => handleSlotClick(slot)}
                  disabled={!availability.available || isSlotPast}
                  className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                    isSlotPast
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : availability.available
                        ? 'bg-green-50 border-green-200 text-green-800 hover:bg-green-100 hover:border-green-300 cursor-pointer'
                        : 'bg-red-50 border-red-200 text-red-800 cursor-not-allowed'
                  }`}
                >
                  <div className="font-semibold">
                    {slot.start_time || slot.start} - {slot.end_time || slot.end}
                  </div>
                  <div className="text-xs mt-1">
                    {isSlotPast
                      ? 'Past'
                      : availability.available
                        ? max_capacity === 1
                          ? 'Available'
                          : `${availability.remainingSlots} spots`
                        : 'Full'
                    }
                  </div>
                </button>
              );
            })}
          </div>

          {availableSlots.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">🕐</div>
              <p>No time slots available for this date</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AmenityBookingCalendar;